import { useState } from 'react';

import { KinsideService, SitterSearchInfoService } from 'us-web-services';
import { Button, ButtonType } from '../../../components';
import { useDependencies } from '../../../dependencies';

interface Props {
  isDaycare: boolean;
  filter: Record<string, string | number | null | undefined>;
  resultCount: number;
}

// Mirrors sitters.tpl.html:159-192 + sitters.js requestMoreSitters /
// requestMoreFacilities. Last-page CTA that lets families flag searches
// returning too few results so the recruiting team can react.
export const NotEnoughCaregiversCard = ({ isDaycare, filter, resultCount }: Props): JSX.Element | null => {
  const { stores } = useDependencies();
  const userId = stores.user.user?.id;
  const [state, setState] = useState<'idle' | 'submitting' | 'submitted' | 'error'>('idle');

  if (!userId) return null;

  const submit = async (): Promise<void> => {
    setState('submitting');
    try {
      if (isDaycare) {
        await KinsideService.saveSearchInfo({
          parentId: userId,
          resultCount,
          parentKinsideSearchDTO: {
            parentId: userId,
            latitude: toNumber(filter.latitude),
            longitude: toNumber(filter.longitude),
            distance: toNumber(filter.distance),
            ageRanges: toStr(filter.ageRanges),
            facilityClass: toFacility(filter.facilityClass),
            scheduleTypes: toStr(filter.scheduleTypes),
            startDate: toStr(filter.startDate ?? filter.start),
          },
        });
      } else {
        await SitterSearchInfoService.create({
          parentId: userId,
          resultCount,
          sitterSearch: filter,
        });
      }
      setState('submitted');
    } catch (err) {
      console.warn('[NotEnoughCaregivers] request failed', err);
      setState('error');
    }
  };

  return (
    <div className='us-card rounded-sm p-5 text-center flex flex-col justify-between'>
      {state === 'submitted' || state === 'error' ? (
        <div>
          {state === 'submitted' && (
            <p className='font-semibold'>
              Thanks! We&apos;ve let our recruiting department know.
            </p>
          )}
          <p>
            {isDaycare
              ? 'Meanwhile, you can post a job to find the perfect sitter or nanny.'
              : 'Meanwhile, try posting to our Job Board, where additional caregivers can see the job and apply.'}
          </p>
          <a href='/app/job-post' className='btn btn-primary w-full mt-4 inline-block text-center'>
            Post a job
          </a>
        </div>
      ) : (
        <>
          <p>
            Not seeing enough {isDaycare ? 'daycares or preschools?' : 'caregivers?'} We
            want to hear from you.
          </p>
          <div className='mt-4'>
            <Button
              buttonType={ButtonType.tertiary}
              disabled={state === 'submitting'}
              onClick={submit}
              className='font-semibold'
            >
              {state === 'submitting' ? (
                <>
                  <i className='fa-solid fa-spinner fa-spin mr-2' />
                  Submitting...
                </>
              ) : (
                'Let us know'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const toStr = (v: unknown): string | undefined =>
  v == null || v === '' ? undefined : String(v);

const toNumber = (v: unknown): number | undefined => {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const toFacility = (v: unknown): 'center' | 'homebased' | undefined => {
  const s = toStr(v);
  return s === 'center' || s === 'homebased' ? s : undefined;
};
